import {Counter, InfoTooltip} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useRouteMatch} from 'react-router-dom';

import {
  useProjectRunsCountQuery,
  useSweepRunsCountQuery,
} from '../../generated/graphql';
import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {GROUP_PATHS} from '../../routes/workspace/groupWorkspacePaths';
import {SWEEP_PATHS} from '../../routes/workspace/sweepWorkspacePaths';
import {maybePluralize} from '../../util/uihelpers';
import {usePinnedRuns} from './RunSelectorRunsContext';
import {isCrossProjectRun} from './util';

function useNumCrossProjectRunsCount(): number {
  const {entityName, projectName} = useProjectParams();
  const pinnedRuns = usePinnedRuns();
  if (!entityName || !projectName || !pinnedRuns) {
    return 0;
  }
  const crossProjectRuns = pinnedRuns.filter(r =>
    isCrossProjectRun(r.project, {entityName, name: projectName})
  );
  return crossProjectRuns.length;
}

const RunsCounterDisplay = ({
  numCurrProjectRuns,
  numCrossProjectRuns,
}: {
  numCurrProjectRuns: number;
  numCrossProjectRuns: number;
}) => {
  const totalCount = numCurrProjectRuns + numCrossProjectRuns;
  const hasCrossProjectRuns = numCrossProjectRuns > 0;
  return (
    <TailwindContents>
      <div className="flex items-center gap-3">
        <Counter
          value={totalCount}
          aria-label="runs"
          aria-describedby={
            hasCrossProjectRuns ? 'runs-counter-info' : undefined
          }
        />
        {hasCrossProjectRuns && (
          <InfoTooltip
            id="runs-counter-info"
            aria-label="Run count details"
            size="sm">
            Includes {maybePluralize(numCrossProjectRuns, 'run')} from other
            projects
          </InfoTooltip>
        )}
      </div>
    </TailwindContents>
  );
};

const ProjectRunsCounter = () => {
  const {entityName, projectName} = useProjectParams();
  const {loading, error, data} = useProjectRunsCountQuery({
    variables: {projectName, entityName},
    skip: entityName == null || projectName == null,
  });
  const crossProjectRunsCount = useNumCrossProjectRunsCount();

  if (loading || error) {
    return null;
  }

  return (
    <RunsCounterDisplay
      numCurrProjectRuns={data?.project?.runCount ?? 0}
      numCrossProjectRuns={crossProjectRunsCount}
    />
  );
};

const SweepRunsCounter = () => {
  const {entityName, projectName, sweepName} = useProjectParams();
  const {loading, error, data} = useSweepRunsCountQuery({
    variables: {projectName, entityName, sweepName: sweepName ?? ''},
    skip: entityName == null || projectName == null || sweepName == null,
  });
  const crossProjectRunsCount = useNumCrossProjectRunsCount();

  if (loading || error) {
    return null;
  }

  return (
    <RunsCounterDisplay
      numCurrProjectRuns={data?.project?.sweep?.runCount ?? 0}
      numCrossProjectRuns={crossProjectRunsCount}
    />
  );
};

export const RunsCounter = () => {
  const isSweepPage = useRouteMatch(SWEEP_PATHS) != null;
  const isGroupPage = useRouteMatch(GROUP_PATHS) != null;

  if (isGroupPage) {
    return null;
  }

  if (isSweepPage) {
    return <SweepRunsCounter />;
  }

  return <ProjectRunsCounter />;
};
